/* PG-016 Cart Drawer Web Component
   ・mgh-cart-drawer タグでカスタムエレメント定義
   ・cart:open / cart:close / cart:updated の CustomEvent を窓口に
   ・Shopify Cart AJAX API（/cart/add.js, /cart/change.js, /cart.js）で更新
   ・Section Rendering API（?section_id=mgh-cart-drawer）で HTML 再描画 */

// [AI] 2026-08-27 (FT-023): カートページの「買い物を続ける」の戻り先。
// タブを閉じるまでの一時的な値なので sessionStorage を使う。
// ドロワーは遷移せず閉じるだけなのでこの値を使わない。
//
// [AI] 2026-08-28: 記録するのは「商品を探していたページ」だけに限定する。
// カート以外を一律で記録すると、ポリシーページ（footer の配送・返品リンク先）や
// 記事・404 まで戻り先になり、「買い物を続ける」で返品ポリシーに戻るなど
// 意図と食い違うため。
const LAST_PAGE_KEY = 'mgh:last-page';
const SHOPPING_PAGE_TYPES = ['index', 'product', 'collection', 'list-collections', 'search'];

function rememberCurrentPage(pageType) {
  if (!SHOPPING_PAGE_TYPES.includes(pageType)) return;
  try {
    sessionStorage.setItem(LAST_PAGE_KEY, window.location.pathname + window.location.search);
  } catch (error) {
    // プライベートブラウジング等で sessionStorage が使えない場合は記録しない
  }
}

// [AI] 2026-08-28 (FT-023): 戻り先の読み出し口。キー名をこのファイルに一本化し、
// カートページのインライン JS からも同じ実装を使う（二重定義を避ける）。
function readLastShoppingPage() {
  try {
    return sessionStorage.getItem(LAST_PAGE_KEY);
  } catch (error) {
    return null;
  }
}

window.mghReadLastShoppingPage = readLastShoppingPage;

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class MghCartDrawer extends HTMLElement {
  connectedCallback() {
    this.backdrop = this.querySelector('[data-mgh-cart-backdrop]');
    this.closeBtn = this.querySelector('[data-mgh-cart-close]');
    this.continueBtn = this.querySelector('[data-mgh-cart-drawer-continue]');
    this.body = this.querySelector('[data-mgh-cart-body]');
    this.footer = this.querySelector('[data-mgh-cart-footer]');
    this.sectionId = this.getAttribute('data-section-id');
    this.currency = this.getAttribute('data-currency') || 'JPY';
    this.pageType = this.getAttribute('data-page-type') || '';
    rememberCurrentPage(this.pageType);

    this._isUpdating = false;
    // [AI] 2026-07-23 (FT-017): 数量変更の楽観的UI用。押下即反映し、応答は裏で同期する。
    this._pendingQty = new Map();
    this._flushing = false;
    this._syncTimer = null;

    this.boundOpen = this.open.bind(this);
    this.boundOnCartOpen = this.onCartOpen.bind(this);
    this.boundClose = this.close.bind(this);
    this.boundOnKeydown = this.onKeydown.bind(this);
    this.boundOnUpdated = this.onCartUpdated.bind(this);
    this.boundOnBodyClick = this.onBodyClick.bind(this);
    this.boundOnBodyChange = this.onBodyChange.bind(this);
    this.boundOnPageshow = this.onPageshow.bind(this);

    if (this.closeBtn) this.closeBtn.addEventListener('click', this.boundClose);
    // [AI] 2026-07-23: 「買い物を続ける」= 商品を探す動作に戻る（詳細→一覧 / それ以外→閉じる、onContinue 参照）
    // フッターはカート更新で innerHTML 再描画されボタン直付けのリスナーが失われるため、
    // 再描画されないルート要素へのイベント委譲で常に動作させる。
    this.boundContinue = this.onContinue.bind(this);
    this.addEventListener('click', this.boundContinue);
    // [AI] 2026-08-27 (FT-023): CTA を submit 化したため、ローディング表示は form の
    // submit で行う。注: disabled = true を同期実行すると button name="checkout" が
    // POST に含まれず Shopify が checkout にリダイレクトしないため、class の付与のみ。
    this.boundOnSubmit = this.onFormSubmit.bind(this);
    this.addEventListener('submit', this.boundOnSubmit);
    if (this.backdrop) this.backdrop.addEventListener('click', this.boundClose);
    document.addEventListener('keydown', this.boundOnKeydown);
    document.addEventListener('cart:open', this.boundOnCartOpen);
    document.addEventListener('cart:close', this.boundClose);
    document.addEventListener('cart:updated', this.boundOnUpdated);
    window.addEventListener('pageshow', this.boundOnPageshow);

    if (this.body) {
      this.body.addEventListener('click', this.boundOnBodyClick);
      this.body.addEventListener('change', this.boundOnBodyChange);
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.boundOnKeydown);
    document.removeEventListener('cart:open', this.boundOnCartOpen);
    document.removeEventListener('cart:close', this.boundClose);
    document.removeEventListener('cart:updated', this.boundOnUpdated);
    window.removeEventListener('pageshow', this.boundOnPageshow);
    if (this.body) {
      this.body.removeEventListener('click', this.boundOnBodyClick);
      this.body.removeEventListener('change', this.boundOnBodyChange);
    }
    if (this.closeBtn) this.closeBtn.removeEventListener('click', this.boundClose);
    this.removeEventListener('click', this.boundContinue);
    this.removeEventListener('submit', this.boundOnSubmit);
    if (this.backdrop) this.backdrop.removeEventListener('click', this.boundClose);
  }

  onBodyClick(event) {
    const removeBtn = event.target.closest('[data-mgh-cart-remove]');
    if (removeBtn) {
      event.preventDefault();
      const line = parseInt(removeBtn.dataset.line, 10);
      const input = this.body.querySelector(`[data-mgh-cart-qty-input][data-line="${line}"]`);
      if (input) this.removeLine(input);
      return;
    }
    const qtyBtn = event.target.closest('[data-mgh-cart-qty]');
    if (qtyBtn) {
      event.preventDefault();
      const line = parseInt(qtyBtn.dataset.line, 10);
      const delta = parseInt(qtyBtn.dataset.delta, 10);
      const input = this.body.querySelector(`[data-mgh-cart-qty-input][data-line="${line}"]`);
      if (!input) return;
      const current = parseInt(input.value, 10) || 0;
      const next = Math.max(0, current + delta);
      this.optimisticQty(input, next);
    }
  }

  onBodyChange(event) {
    const input = event.target.closest('[data-mgh-cart-qty-input]');
    if (!input) return;
    const value = Math.max(0, parseInt(input.value, 10) || 0);
    // [AI] 2026-07-23 (FT-017): 0 は削除扱い。楽観的に消さずローディング表示で更新する。
    if (value <= 0) this.removeLine(input);
    else this.optimisticQty(input, value);
  }

  // [AI] 2026-08-27 (FT-023): 通常チェックアウトの送信時にローディング表示。
  // ウォレットボタンは Shopify 側が独自に処理するため対象外。
  onFormSubmit(event) {
    if (!event.target.closest('[data-mgh-cart-drawer-form]')) return;
    const button = this.querySelector('[data-mgh-cart-drawer-checkout]');
    if (button) button.classList.add('is-loading');
  }

  // [AI] 2026-05-12: bfcache でバック遷移した時にチェックアウトボタンの
  // is-loading 状態をリセット
  onPageshow() {
    const checkout = this.querySelector('[data-mgh-cart-drawer-checkout]');
    if (checkout) checkout.classList.remove('is-loading');
  }

  // [AI] 2026-07-23 (FT-017): cart:open を受けて開く。detail.loading の場合はローディング表示。
  // 追加処理の応答を待たず即座に開くことで体感速度を改善する。
  onCartOpen(event) {
    const detail = event && event.detail ? event.detail : {};
    this.open();
    if (detail.loading) this.showLoading();
  }

  // [AI] 2026-07-23 (FT-017): 読込中はオーバーレイ下の古い操作要素（数量/削除/チェックアウト）を
  // 無効化し、二重リクエストや状態誤認を防ぐ。button/input は disabled、リンクは tabindex=-1 +
  // aria-disabled（+ CSS pointer-events）で、マウス・キーボード・支援技術いずれからも到達不可にする。
  showLoading() {
    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');
    this.setContentDisabled(true);
  }

  hideLoading() {
    this.classList.remove('is-loading');
    this.removeAttribute('aria-busy');
    this.setContentDisabled(false);
  }

  setContentDisabled(disabled) {
    const scopes = [this.body, this.footer];
    scopes.forEach((scope) => {
      if (!scope) return;
      scope.querySelectorAll('button, input, select, textarea').forEach((el) => {
        el.disabled = disabled;
      });
      scope.querySelectorAll('a[href]').forEach((el) => {
        if (disabled) {
          el.setAttribute('tabindex', '-1');
          el.setAttribute('aria-disabled', 'true');
        } else {
          el.removeAttribute('tabindex');
          el.removeAttribute('aria-disabled');
        }
      });
    });
  }

  open() {
    this.previousFocus = document.activeElement;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    if (this.closeBtn) this.closeBtn.focus();
  }

  close() {
    this.classList.remove('is-open');
    this.hideLoading();
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
      this.previousFocus = null;
    }
  }

  // [AI] 2026-07-23: 「買い物を続ける」= ドロワーを閉じて今のページに留まるだけ。
  // [AI] 2026-08-27 (FT-023): ドロワーの「買い物を続ける」は常に閉じるだけ。
  // 遷移させると今いるページから引き剥がされるため、行き先の判定は行わない。
  // （4d684cf で referrer 判定を撤去したときの方針を維持する）
  onContinue(event) {
    const btn = event.target.closest('[data-mgh-cart-drawer-continue]');
    if (!btn) return;
    this.close();
  }

  onKeydown(event) {
    if (!this.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key === 'Tab') {
      this.handleTab(event);
    }
  }

  handleTab(event) {
    const focusables = Array.from(this.querySelectorAll(FOCUSABLE_SELECTOR))
      // [AI] 2026-07-23 (FT-017): 非表示要素と、読込中に無効化した要素（tabindex=-1 の
      // リンク / disabled ボタン）はフォーカストラップから除外する
      .filter((el) => el.offsetParent !== null && el.getAttribute('tabindex') !== '-1');
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  async onCartUpdated(event) {
    const detail = event && event.detail ? event.detail : {};
    // [AI] 2026-07-23 (FT-017): 追加時はドロワーを先に開いてから中身を差し替える（体感速度）
    if (detail.openDrawer) this.open();

    const cart = detail.cart || null;
    // [AI] 2026-07-23 (FT-017): Bundled Section Rendering の HTML が同梱されていれば
    // それを使って描画し、別 fetch を省く。無ければ従来の refresh() にフォールバック。
    const sectionHTML = this.extractSectionHTML(detail);
    if (sectionHTML != null) {
      this.applySectionHTML(sectionHTML, cart);
      // [AI] 2026-07-23 (FT-017): cart が無い場合は同梱HTMLのヘッダー件数からバッジを更新
      if (cart) this.updateHeaderBadge(cart);
      else this.updateHeaderBadgeFromDrawer();
    } else if (cart) {
      await this.refreshFromCart(cart);
    } else {
      await this.refresh();
    }
    // [AI] 2026-07-23 (FT-017): 中身が入ったのでローディングオーバーレイを解除
    this.hideLoading();
  }

  // [AI] 2026-07-23 (FT-017): cart:updated detail から section HTML 文字列を取り出す。
  // add.js は detail.sections、change.js は detail.cart.sections に格納される想定。
  extractSectionHTML(detail) {
    const fromDetail = detail && detail.sections ? detail.sections[this.sectionId] : undefined;
    const fromCart = detail && detail.cart && detail.cart.sections
      ? detail.cart.sections[this.sectionId]
      : undefined;
    const html = fromDetail != null ? fromDetail : fromCart;
    return typeof html === 'string' ? html : null;
  }

  // [AI] 2026-07-23 (FT-017): section HTML 文字列を DOM に反映する共有処理。
  applySectionHTML(html, cart) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const next = doc.querySelector('mgh-cart-drawer') || doc.body;
    if (!next) return;
    const nextBody = next.querySelector('[data-mgh-cart-body]');
    const nextFooter = next.querySelector('[data-mgh-cart-footer]');
    const nextHeader = next.querySelector('[data-mgh-cart-header-count]');
    if (nextBody && this.body) this.body.innerHTML = nextBody.innerHTML;
    const currentFooter = this.querySelector('[data-mgh-cart-footer]');
    if (nextFooter && currentFooter) {
      // [AI] 2026-08-27 (FT-023): footer は innerHTML を差し替えない。
      // accelerated checkout ボタン（content_for_additional_checkout_buttons）は
      // Shopify のスクリプトが後から差し込むため、差し替えると失われ再初期化が必要になる。
      // 代わりにサーバーが返した新 HTML から表示値だけを取り出して反映する。
      this.syncFooterValues(nextFooter);
      currentFooter.removeAttribute('hidden');
      const itemCount = cart && typeof cart.item_count !== 'undefined'
        ? (typeof cart.item_count === 'number' ? cart.item_count : parseInt(cart.item_count, 10) || 0)
        : (nextFooter.classList.contains('is-empty') ? 0 : 1);
      currentFooter.classList.toggle('is-empty', itemCount === 0);
    }
    const currentHeader = this.querySelector('[data-mgh-cart-header-count]');
    if (nextHeader && currentHeader) currentHeader.textContent = nextHeader.textContent;
  }

  // [AI] 2026-08-27 (FT-023): サーバーが返した footer から表示値のみを取り込む。
  // DOM を作り替えないため、Shopify が差し込んだウォレットボタンはそのまま残る。
  syncFooterValues(nextFooter) {
    const pairs = [
      ['[data-mgh-cart-subtotal-money]', 'text'],
      ['[data-mgh-free-shipping]', 'freeShipping'],
    ];
    pairs.forEach(([selector, kind]) => {
      const next = nextFooter.querySelector(selector);
      const current = this.querySelector(selector);
      if (!next || !current) return;
      current.textContent = next.textContent;
      if (kind === 'freeShipping') {
        current.classList.toggle('is-reached', next.classList.contains('is-reached'));
      }
    });
  }

  async refresh(cart) {
    if (!this.sectionId) return;
    try {
      const response = await fetch(`${window.location.pathname}?section_id=${this.sectionId}`, {
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) return;
      const html = await response.text();
      this.applySectionHTML(html, cart);
    } catch (error) {
      // silent: drawer keeps last state on failure
    }
  }

  async refreshFromCart(cart) {
    await this.refresh(cart);
    this.updateHeaderBadge(cart);
  }

  // [AI] 2026-07-23 (FT-017): ドロワー内ヘッダー件数（例 "(3)"）からグローバルバッジを更新。
  // 件数は Liquid の {{ cart.item_count }}（桁区切り無しの整数）を描画しているため、
  // 最初の数字列の抽出で十分。桁区切り書式を導入する場合はここも見直すこと。
  updateHeaderBadgeFromDrawer() {
    const headerCount = this.querySelector('[data-mgh-cart-header-count]');
    if (!headerCount) return;
    const match = headerCount.textContent.match(/\d+/);
    if (!match) return;
    this.updateHeaderBadge({ item_count: parseInt(match[0], 10) });
  }

  updateHeaderBadge(cart) {
    if (!cart) return;
    const badge = document.querySelector('[data-mgh-cart-count]');
    if (!badge) return;
    const count = typeof cart.item_count === 'number' ? cart.item_count : parseInt(cart.item_count, 10) || 0;
    badge.textContent = count;
    badge.classList.toggle('is-empty', count === 0);
  }

  // [AI] 2026-07-23 (FT-017): 数量変更の楽観的UI。
  // 押した瞬間に数量・行金額・小計・件数・バッジをローカル反映し、サーバー更新は
  // デバウンスして裏で実行。連打しても最終値がすぐ反映され、応答待ちで無反応にならない。
  optimisticQty(input, quantity) {
    const key = input.dataset.key;
    const unit = parseInt(input.dataset.unitPrice, 10) || 0;
    const row = input.closest('.mgh-cart-drawer__item');
    if (quantity <= 0) {
      if (row) row.hidden = true;
    } else {
      input.value = quantity;
      if (row) {
        const dec = row.querySelector('[data-mgh-cart-qty][data-delta="-1"]');
        if (dec) dec.disabled = quantity <= 1;
        const lineMoney = row.querySelector('[data-mgh-cart-line-money]');
        const formatted = this.formatMoney(unit * quantity);
        if (lineMoney && formatted != null) lineMoney.textContent = formatted;
      }
    }
    this.recomputeTotals();
    if (key) {
      this._pendingQty.set(key, quantity);
      this.scheduleFlush();
    }
  }

  // [AI] 2026-07-23 (FT-017): 表示中の行から件数・小計を再計算し、ヘッダー件数/バッジ/小計へ反映
  recomputeTotals() {
    let count = 0;
    let subtotal = 0;
    this.body.querySelectorAll('.mgh-cart-drawer__item').forEach((row) => {
      if (row.hidden) return;
      const inp = row.querySelector('[data-mgh-cart-qty-input]');
      if (!inp) return;
      const q = parseInt(inp.value, 10) || 0;
      const unit = parseInt(inp.dataset.unitPrice, 10) || 0;
      count += q;
      subtotal += unit * q;
    });
    const header = this.querySelector('[data-mgh-cart-header-count]');
    if (header) header.textContent = `(${count})`;
    this.updateHeaderBadge({ item_count: count });
    const sub = this.querySelector('[data-mgh-cart-subtotal-money]');
    const formatted = this.formatMoney(subtotal);
    if (sub && formatted != null) sub.textContent = formatted;
    this.updateFreeShipping(subtotal);
  }

  // [AI] 2026-08-27 (FT-023): 送料無料しきい値までの残額を楽観更新する。
  // しきい値・文言テンプレートは Liquid が data 属性で渡す（未設定なら要素自体が無い）。
  // JPY 以外は formatMoney が null を返すため、サーバー確定（refresh）に委ねて何もしない。
  updateFreeShipping(subtotal) {
    const el = this.querySelector('[data-mgh-free-shipping]');
    if (!el) return;
    const threshold = parseInt(el.dataset.threshold, 10) || 0;
    if (threshold <= 0) return;
    const remaining = threshold - subtotal;
    if (remaining > 0) {
      const formatted = this.formatMoney(remaining);
      if (formatted == null) return;
      el.textContent = (el.dataset.template || '').replace('__AMOUNT__', formatted);
      el.classList.remove('is-reached');
    } else {
      el.textContent = el.dataset.reached || '';
      el.classList.add('is-reached');
    }
  }

  // [AI] 2026-07-23 (FT-017): テーマの `| money`（例 "¥1,980"）を JS 側で再現。
  // 金額はセント単位（JPY は amount×100）。JPY 以外は楽観更新せず（null）サーバー確定に委ねる。
  formatMoney(cents) {
    if (this.currency === 'JPY') {
      return `¥${Math.round(cents / 100).toLocaleString('ja-JP')}`;
    }
    return null;
  }

  // [AI] 2026-07-23 (FT-017): 削除（ゴミ箱 / 数量0）はローディング表示にして更新後の
  // 結果（空カート表示など）を出す。楽観的に行を隠すと一瞬空白になるため。
  removeLine(input) {
    const key = input.dataset.key;
    if (!key) return;
    this.showLoading();
    this._pendingQty.set(key, 0);
    if (this._syncTimer) {
      clearTimeout(this._syncTimer);
      this._syncTimer = null;
    }
    this.flush();
  }

  scheduleFlush() {
    if (this._syncTimer) clearTimeout(this._syncTimer);
    // 連打をまとめてから1回だけ送る（各行の最終値のみ反映）
    this._syncTimer = setTimeout(() => this.flush(), 300);
  }

  async flush() {
    if (this._flushing) return;
    this._flushing = true;
    let lastCart = null;
    let failed = false;
    try {
      // 保留が尽きるまで（連打で増えても）送り切る
      while (this._pendingQty.size > 0) {
        const entries = Array.from(this._pendingQty.entries());
        this._pendingQty.clear();
        for (const [key, qty] of entries) {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            // 行キー(id)で指定し、行番号ズレの影響を受けないようにする。sections 同梱で再描画も1本
            body: JSON.stringify({
              id: key,
              quantity: qty,
              sections: this.sectionId,
              sections_url: window.location.pathname,
            }),
          });
          if (!response.ok) {
            failed = true;
            continue;
          }
          lastCart = await response.json();
        }
      }
    } catch (error) {
      failed = true;
    }
    this._flushing = false;
    // 途中で新たな操作が入っていたら再度フラッシュ
    if (this._pendingQty.size > 0) {
      this.scheduleFlush();
      return;
    }
    // 操作が落ち着いたのでサーバー確定値で整合を取る（楽観表示のズレ・税・空表示を補正）
    if (lastCart) {
      const html = lastCart.sections ? lastCart.sections[this.sectionId] : null;
      if (typeof html === 'string') this.applySectionHTML(html, lastCart);
      this.updateHeaderBadge(lastCart);
    } else if (failed) {
      await this.refresh();
    }
    // [AI] 2026-07-23 (FT-017): 削除時に出したローディングを解除（数量+/-時は非表示なので no-op）
    this.hideLoading();
  }
}

if (!customElements.get('mgh-cart-drawer')) {
  customElements.define('mgh-cart-drawer', MghCartDrawer);
}
